import {isNotNil} from 'ramda';

import {VehicleFinancingType, useGetSaleVehicleFinancingQuery} from '@dms/api/vehicle';

import {configurationToFinancingBody} from '../utils/financingBody/configurationToFinancingBody';

interface UseFinancingOverviewParams {
  saleVehicleId: string;
  activeType: VehicleFinancingType;
  currency: string;
  isCreateAllowed: boolean;
  isUpdateAllowed: boolean;
  isGermanTenant: boolean;
}

/**
 * The persisted side of the financing panel: what the vehicle's financing overview says, in the
 * shape the editors work with, and the write permission that applies to the active type.
 */
export function useFinancingOverview(params: UseFinancingOverviewParams) {
  const {
    data: overview,
    isLoading,
    isError,
  } = useGetSaleVehicleFinancingQuery({saleVehicleId: params.saleVehicleId});

  const getPersistedBody = (type: VehicleFinancingType) => {
    const persistedConfiguration = overview?.configurations.find((item) => item.type === type);

    return isNotNil(persistedConfiguration)
      ? configurationToFinancingBody(persistedConfiguration, {
          isGermanTenant: params.isGermanTenant,
        })
      : null;
  };

  const getFinancingState = (type: VehicleFinancingType) =>
    overview?.states.find((item) => item.type === type)?.state ?? 'NONE';

  const configuration = overview?.configurations.find((item) => item.type === params.activeType);
  const financingState = getFinancingState(params.activeType);
  const isPersisted = isNotNil(configuration) && (configuration.persisted ?? true);

  return {
    isLoading,
    isError,
    financingState,
    getFinancingState,
    getPersistedBody,
    persistedBody: getPersistedBody(params.activeType),
    isPersisted,
    hasFinancingInEffect: isPersisted || financingState === 'AUTOMATIC',
    isWriteAllowed: isPersisted ? params.isUpdateAllowed : params.isCreateAllowed,
    vehiclePrice: Number(overview?.vehiclePrice?.amount ?? 0),
    // The amount string carries the currency scale the backend prices at ("100000.00").
    vehiclePriceWithVat: overview?.vehiclePrice ?? null,
    currency: overview?.vehiclePrice?.currency ?? params.currency,
  };
}
